/* Entry by intent, as a top-bar disclosure menu.
 *
 * A disclosure, not an ARIA menu: the panel holds destinations, so Tab must keep
 * working normally (W3C APG). Escape closes it and returns focus to the trigger;
 * a click outside closes it too.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES, chaptersOf, routeDone, routeEntry } from '../engine/routes';
import { pad } from '../engine/progress';
import { useApp } from '../state/AppProvider';
import { useNarrow } from '../hooks/useMedia';
import '../styles/subjects.css';

export function EnterByMenu() {
  const { t } = useTranslation();
  const { progress, selectNode } = useApp();
  const narrow = useNarrow();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    const onDown = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="route-menu" ref={wrap}>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        data-act="enter-by"
        ref={trigger}
        aria-expanded={open}
        aria-controls="enter-by-menu"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {t(narrow ? 'routes:short' : 'routes:title')}
        <span aria-hidden="true"> ▾</span>
      </button>

      <div className="route-menu-panel" id="enter-by-menu" data-od-id="enter-by-menu" hidden={!open}>
        <ul>
          {ROUTES.map((route) => {
            const chapters = chaptersOf(route);
            const entry = routeEntry(route, progress);
            const blocked = entry.kind === 'blocked';
            const meta = entry.kind === 'open'
              ? `${routeDone(route, progress)}/${chapters.length} · ${t(route.whoKey)}`
              : blocked
                ? entry.by
                  ? `${t('after')} ${pad(entry.by)} · ${t(entry.by.titleKey)}`
                  : t(entry.chapter.titleKey)
                : t('legendDone');

            return (
              <li key={route.id}>
                <button
                  type="button"
                  className="route-row"
                  data-act="route"
                  data-id={route.id}
                  data-state={entry.kind}
                  aria-label={`${t(route.labelKey)} · ${meta}`}
                  onClick={() => {
                    if (entry.kind === 'done') return;
                    selectNode(entry.chapter.id);
                    setOpen(false);
                  }}
                >
                  <span className="route-label">{t(route.labelKey)}</span>
                  <span className="route-meta meta">{meta}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
